import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { get, post, del } from 'aws-amplify/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer } from 'recharts';
import './Home.css';

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

// 秒 → 「3時間12分」「45分」
const formatDuration = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}時間${m}分`;
  return `${m}分`;
};

// 経過時間 → 「01:02:03」
const formatClock = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

// 'YYYY-MM-DD' → 「10/12(月)」
const formatShortDate = (ds) => {
  const [y, m, d] = ds.split('-').map(Number);
  const w = WEEKDAYS[new Date(y, m - 1, d).getDay()];
  return `${m}/${d}(${w})`;
};

const formatDateTime = (iso) => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 11) return 'おはようございます';
  if (h < 18) return 'こんにちは';
  return 'おつかれさまです';
};

const rateOf = (done, assigned) => (assigned > 0 ? Math.round((done / assigned) * 100) : null);

// API エラー本文を取り出す
const readApiError = async (e, fallback) => {
  try { const b = await e.response.body.json(); return b.error || fallback; } catch { return fallback; }
};

// 進捗メーター（完了数 / 割当数）。達成したら緑＋「✓ 達成」
function Meter({ value, max }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const done = max > 0 && value >= max;
  return (
    <div className="home-meter" role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
      <div className={`home-meter-fill ${done ? 'done' : ''}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function TaskDot({ color }) {
  return <span className="home-task-dot" style={{ background: color || '#9CA3AF' }} aria-hidden="true" />;
}

// 直近7日間の作業時間（今日を強調）
function WeekChart({ week }) {
  const data = week.map(w => ({
    ...w,
    label: formatShortDate(w.date),
    hours: Math.round((w.seconds / 3600) * 10) / 10,
  }));
  const todayIdx = data.length - 1;
  const hasData = data.some(d => d.seconds > 0);

  const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="home-chart-tooltip">
        <div className="home-chart-tooltip-date">{d.label}</div>
        <div>作業時間 <strong>{formatDuration(d.seconds)}</strong></div>
        <div>完了数 <strong>{d.completed.toLocaleString()}件</strong></div>
      </div>
    );
  };

  if (!hasData) {
    return <p className="home-empty">直近7日間の作業記録はまだありません</p>;
  }

  return (
    <div className="home-chart" aria-label="直近7日間の作業時間">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#F3F4F6" />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
          <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}h`} allowDecimals={false} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
          <Bar dataKey="hours" radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive={false}>
            {data.map((d, i) => (
              <Cell key={d.date} fill={i === todayIdx ? '#3B82F6' : '#CBD5E1'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="home-chart-legend">
        <span><i style={{ background: '#3B82F6' }} />今日</span>
        <span><i style={{ background: '#CBD5E1' }} />過去6日間</span>
      </div>
    </div>
  );
}

// 管理者：指示の投稿ダイアログ
function AnnouncementComposer({ members, onClose, onPosted }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [targetMode, setTargetMode] = useState('all');
  const [targets, setTargets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleTarget = (id) => setTargets(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('タイトルを入力してください'); return; }
    if (targetMode === 'users' && targets.length === 0) { setError('宛先のユーザーを選んでください'); return; }
    setSaving(true);
    setError('');
    try {
      const op = post({
        apiName: 'worktimerApi',
        path: '/tenants/announcements',
        options: { body: { title: title.trim(), body: body.trim(), priority, target_user_ids: targetMode === 'all' ? null : targets } },
      });
      await op.response;
      onPosted();
    } catch (err) {
      setError(await readApiError(err, '投稿に失敗しました。もう一度お試しください。'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-overlay">
      <form className="dialog home-composer" onSubmit={submit}>
        <div className="dialog-header"><h2>📢 指示・お知らせを投稿</h2></div>
        <div className="dialog-body">
          <label className="home-field">
            <span>タイトル</span>
            <input type="text" value={title} maxLength={200} onChange={e => setTitle(e.target.value)} placeholder="例：本日は15時から棚卸しを行います" autoFocus />
          </label>
          <label className="home-field">
            <span>本文（任意）</span>
            <textarea rows={5} value={body} maxLength={5000} onChange={e => setBody(e.target.value)} placeholder="詳しい内容や手順を書いてください" />
          </label>

          <div className="home-field">
            <span>重要度</span>
            <div className="home-segment">
              <button type="button" className={priority === 'normal' ? 'active' : ''} onClick={() => setPriority('normal')}>通常</button>
              <button type="button" className={priority === 'important' ? 'active important' : ''} onClick={() => setPriority('important')}>⚠ 重要</button>
            </div>
          </div>

          <div className="home-field">
            <span>宛先</span>
            <div className="home-segment">
              <button type="button" className={targetMode === 'all' ? 'active' : ''} onClick={() => setTargetMode('all')}>全員</button>
              <button type="button" className={targetMode === 'users' ? 'active' : ''} onClick={() => setTargetMode('users')}>ユーザーを選ぶ</button>
            </div>
            {targetMode === 'users' && (
              <div className="home-target-list">
                {members.map(m => (
                  <label key={m.user_id} className="home-target-item">
                    <input type="checkbox" checked={targets.includes(m.user_id)} onChange={() => toggleTarget(m.user_id)} />
                    {m.display_name}
                  </label>
                ))}
              </div>
            )}
          </div>
          {error && <p className="home-error">{error}</p>}
        </div>
        <div className="dialog-footer">
          <button type="button" className="dialog-cancel" onClick={onClose} disabled={saving}>キャンセル</button>
          <button type="submit" className="dialog-save" disabled={saving}>
            {saving ? (<><span className="btn-inline-spinner" />投稿中...</>) : '投稿する'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Home({ user, onStartTask, onNavigate }) {
  const isAdmin = user?.role === 'admin';
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [annError, setAnnError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [now, setNow] = useState(Date.now());

  const fetchSummary = useCallback(async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/worklogs/home-summary' });
      const res = await op.response;
      setSummary(await res.body.json());
      setSummaryError('');
    } catch (e) {
      console.error('❌ HOMEサマリー取得エラー:', e);
      setSummaryError('データの取得に失敗しました');
    }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/tenants/announcements' });
      const res = await op.response;
      const data = await res.body.json();
      setAnnouncements(Array.isArray(data) ? data : []);
      setAnnError('');
    } catch (e) {
      console.error('❌ 指示一覧取得エラー:', e);
      setAnnError('指示を取得できませんでした');
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([fetchSummary(), fetchAnnouncements()]);
    setLoading(false);
  }, [fetchSummary, fetchAnnouncements]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60 * 1000); // 1分ごとに更新
    return () => clearInterval(t);
  }, [refresh]);

  // 計測中タスクの経過時間表示用
  useEffect(() => {
    if (!summary?.active_session) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [summary?.active_session]);

  const markRead = async (id) => {
    setAnnouncements(list => list.map(a => (a.id === id ? { ...a, is_read: true } : a)));
    try {
      const op = post({ apiName: 'worktimerApi', path: `/tenants/announcements/${id}/read`, options: { body: {} } });
      await op.response;
    } catch (e) {
      console.error('❌ 既読登録エラー:', e);
      fetchAnnouncements();
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const op = del({ apiName: 'worktimerApi', path: `/tenants/announcements/${deleteTarget.id}` });
      await op.response;
      await fetchAnnouncements();
    } catch (e) {
      console.error('❌ 指示削除エラー:', e);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const today = summary?.today;
  const yesterday = summary?.yesterday;
  const active = summary?.active_session;
  const activeElapsed = active ? Math.floor((now - new Date(active.start_at).getTime()) / 1000) : 0;
  const unreadCount = announcements.filter(a => !a.is_read).length;
  const yesterdayRate = yesterday ? rateOf(yesterday.total_completed, yesterday.total_assigned) : null;
  const todayRate = today ? rateOf(today.total_completed, today.total_assigned) : null;

  // タスク一覧をフォルダごとにまとめる
  const taskGroups = useMemo(() => {
    const groups = new Map();
    for (const t of summary?.tasks || []) {
      const key = t.folder_name || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    }
    return [...groups.entries()];
  }, [summary?.tasks]);

  const todayLabel = (() => {
    const d = new Date();
    return `${d.getMonth() + 1}月${d.getDate()}日（${WEEKDAYS[d.getDay()]}）`;
  })();

  if (loading) {
    return (
      <div className="home">
        <div className="home-loading"><div className="loading-spinner" />読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="home">
      {/* あいさつ */}
      <header className="home-hero">
        <div>
          <p className="home-date">{todayLabel}</p>
          <h1 className="home-greeting">{greeting()}、{summary?.me?.display_name || user?.display_name || ''}さん</h1>
        </div>
        <button className="home-refresh" onClick={refresh} title="最新の情報に更新">⟳ 更新</button>
      </header>

      {summaryError && <p className="home-error home-error-banner">{summaryError}</p>}

      {/* 計測中のタスク */}
      {active && (
        <div className="home-active-banner">
          <span className="home-live-dot" aria-hidden="true" />
          <div className="home-active-text">
            <span className="home-active-label">計測中</span>
            <strong>{active.task_name}</strong>
          </div>
          <span className="home-active-clock">{formatClock(activeElapsed)}</span>
          <button className="home-btn-primary" onClick={() => onStartTask(null)}>タイマーを開く</button>
        </div>
      )}

      {/* KPI */}
      <section className="home-kpis" aria-label="今日のサマリー">
        <div className="home-kpi">
          <span className="home-kpi-label">本日の作業時間</span>
          <span className="home-kpi-value">{formatDuration(today?.total_seconds)}</span>
          <span className="home-kpi-sub">昨日 {formatDuration(yesterday?.total_seconds)}</span>
        </div>
        <div className="home-kpi">
          <span className="home-kpi-label">本日の完了数</span>
          <span className="home-kpi-value">
            {(today?.total_completed || 0).toLocaleString()}
            {today?.total_assigned > 0 && <small> / {today.total_assigned.toLocaleString()}件</small>}
          </span>
          {today?.total_assigned > 0
            ? <Meter value={today.total_completed} max={today.total_assigned} />
            : <span className="home-kpi-sub">本日の割り当てなし</span>}
        </div>
        <div className="home-kpi">
          <span className="home-kpi-label">昨日の達成率</span>
          <span className="home-kpi-value">{yesterdayRate === null ? '—' : `${yesterdayRate}%`}</span>
          <span className="home-kpi-sub">
            {yesterday?.total_assigned > 0
              ? `${yesterday.total_completed.toLocaleString()} / ${yesterday.total_assigned.toLocaleString()}件 完了`
              : '昨日の割り当てなし'}
          </span>
        </div>
        <button className={`home-kpi home-kpi-button ${!isAdmin && unreadCount > 0 ? 'has-unread' : ''}`}
          onClick={() => document.getElementById('home-announcements')?.scrollIntoView({ behavior: 'smooth' })}>
          <span className="home-kpi-label">{isAdmin ? '投稿中の指示' : '未読の指示'}</span>
          <span className="home-kpi-value">{isAdmin ? announcements.length : unreadCount}<small> 件</small></span>
          <span className="home-kpi-sub">{isAdmin ? '直近60日間' : unreadCount > 0 ? '確認してください →' : 'すべて確認済み'}</span>
        </button>
      </section>

      <div className="home-grid">
        {/* 管理者からの指示 */}
        <section className="home-card home-span-2" id="home-announcements">
          <div className="home-card-header">
            <h2>📢 管理者からの指示</h2>
            {isAdmin && <button className="home-btn-primary" onClick={() => setShowComposer(true)}>＋ 投稿する</button>}
          </div>
          {annError ? (
            <p className="home-error">{annError}</p>
          ) : announcements.length === 0 ? (
            <p className="home-empty">{isAdmin ? 'まだ投稿はありません。「投稿する」から全員や特定のユーザーに指示を出せます。' : '現在、指示はありません'}</p>
          ) : (
            <ul className="home-ann-list">
              {announcements.map(a => {
                const long = (a.body || '').length > 140;
                const open = expanded[a.id];
                return (
                  <li key={a.id} className={`home-ann ${a.priority === 'important' ? 'important' : ''} ${!a.is_read && !isAdmin ? 'unread' : ''}`}>
                    <div className="home-ann-badges">
                      {a.priority === 'important' && <span className="home-badge important">⚠ 重要</span>}
                      {!isAdmin && a.is_personal && <span className="home-badge personal">👤 あなた宛て</span>}
                      {isAdmin && (
                        <span className="home-badge neutral">
                          {a.target_names ? `宛先：${a.target_names.join('、')}` : '宛先：全員'}
                        </span>
                      )}
                      {!isAdmin && !a.is_read && <span className="home-badge new">未読</span>}
                    </div>
                    <h3 className="home-ann-title">{a.title}</h3>
                    {a.body && (
                      <p className={`home-ann-body ${long && !open ? 'clamped' : ''}`}>{a.body}</p>
                    )}
                    {long && (
                      <button className="home-link" onClick={() => setExpanded(x => ({ ...x, [a.id]: !open }))}>
                        {open ? '閉じる' : 'つづきを読む'}
                      </button>
                    )}
                    <div className="home-ann-meta">
                      <span>{a.author_name} ・ {formatDateTime(a.created_at)}</span>
                      {isAdmin ? (
                        <span className="home-ann-actions">
                          <span>既読 {a.read_count} / {a.target_count}人</span>
                          <button className="home-link danger" onClick={() => setDeleteTarget(a)}>削除</button>
                        </span>
                      ) : !a.is_read ? (
                        <button className="home-btn-secondary" onClick={() => markRead(a.id)}>✓ 確認しました</button>
                      ) : (
                        <span className="home-read-mark">✓ 確認済み</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* 本日やること */}
        <section className="home-card">
          <div className="home-card-header">
            <h2>✅ 本日やること</h2>
            {todayRate !== null && <span className="home-card-note">達成率 {todayRate}%</span>}
          </div>
          {!today || today.tasks.filter(t => t.assigned_count > 0).length === 0 ? (
            <p className="home-empty">本日の割り当てはありません。<br />下のタスク一覧から作業を始められます。</p>
          ) : (
            <ul className="home-todo-list">
              {today.tasks.filter(t => t.assigned_count > 0).map(t => {
                const done = t.completed_count >= t.assigned_count;
                return (
                  <li key={t.task_id} className="home-todo">
                    <div className="home-todo-head">
                      <TaskDot color={t.color} />
                      <span className="home-todo-name">{t.task_name}</span>
                      <span className="home-todo-count">
                        {done && <span className="home-done-label">✓ 達成</span>}
                        {t.completed_count} / {t.assigned_count}件
                      </span>
                    </div>
                    <Meter value={t.completed_count} max={t.assigned_count} />
                    <div className="home-todo-foot">
                      <span>{formatDuration(t.seconds)}</span>
                      {!done && <button className="home-link" onClick={() => onStartTask(t.task_id)}>▶ 始める</button>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* 昨日の実行結果 */}
        <section className="home-card">
          <div className="home-card-header">
            <h2>📋 昨日の実行結果</h2>
            {yesterday && <span className="home-card-note">{formatShortDate(yesterday.date)}</span>}
          </div>
          {!yesterday || yesterday.tasks.length === 0 ? (
            <p className="home-empty">昨日の作業記録はありません</p>
          ) : (
            <>
              <div className="home-mini-stats">
                <div><span>作業時間</span><strong>{formatDuration(yesterday.total_seconds)}</strong></div>
                <div><span>完了数</span><strong>{yesterday.total_completed.toLocaleString()}件</strong></div>
                <div><span>達成率</span><strong>{yesterdayRate === null ? '—' : `${yesterdayRate}%`}</strong></div>
              </div>
              <table className="home-table">
                <thead>
                  <tr><th>タスク</th><th className="num">完了 / 割当</th><th className="num">時間</th></tr>
                </thead>
                <tbody>
                  {yesterday.tasks.map(t => (
                    <tr key={t.task_id}>
                      <td><TaskDot color={t.color} />{t.task_name}</td>
                      <td className="num">
                        {t.completed_count}{t.assigned_count > 0 ? ` / ${t.assigned_count}` : ''}
                        {t.assigned_count > 0 && t.completed_count >= t.assigned_count && <span className="home-done-label"> ✓</span>}
                      </td>
                      <td className="num">{formatDuration(t.seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>

        {/* 直近7日間 */}
        <section className="home-card home-span-2">
          <div className="home-card-header">
            <h2>📈 直近7日間の作業時間</h2>
            <button className="home-link" onClick={() => onNavigate('dashboard')}>ダッシュボードへ →</button>
          </div>
          <WeekChart week={summary?.week || []} />
        </section>

        {/* 管理者：チーム状況 */}
        {isAdmin && summary?.team && (
          <section className="home-card home-span-3">
            <div className="home-card-header">
              <h2>👥 チームの状況</h2>
              <span className="home-card-note">作業中 {summary.team.active_now.length}人 / {summary.team.members.length}人</span>
            </div>
            {summary.team.active_now.length > 0 && (
              <div className="home-active-chips">
                {summary.team.active_now.map(a => (
                  <span key={a.user_id} className="home-active-chip">
                    <span className="home-live-dot" aria-hidden="true" />
                    <strong>{a.display_name}</strong>{a.task_name}
                    <span className="home-chip-time">{formatDuration(Math.floor((now - new Date(a.start_at).getTime()) / 1000))}</span>
                  </span>
                ))}
              </div>
            )}
            <div className="home-table-wrap">
              <table className="home-table">
                <thead>
                  <tr>
                    <th>メンバー</th>
                    <th>本日の進捗</th>
                    <th className="num">本日の作業時間</th>
                    <th className="num">昨日の達成率</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.team.members.map(m => {
                    const yRate = rateOf(m.yesterday.completed, m.yesterday.assigned);
                    return (
                      <tr key={m.user_id}>
                        <td>
                          {m.is_active && <span className="home-live-dot small" title="作業中" />}
                          {m.display_name}
                        </td>
                        <td className="home-progress-cell">
                          {m.today.assigned > 0 ? (
                            <>
                              <Meter value={m.today.completed} max={m.today.assigned} />
                              <span className="home-progress-num">{m.today.completed} / {m.today.assigned}</span>
                            </>
                          ) : (
                            <span className="home-muted">{m.today.completed > 0 ? `${m.today.completed}件完了（割当なし）` : '割当なし'}</span>
                          )}
                        </td>
                        <td className="num">{formatDuration(m.today.seconds)}</td>
                        <td className="num">{yRate === null ? '—' : `${yRate}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* タスク一覧 */}
        <section className="home-card home-span-3">
          <div className="home-card-header">
            <h2>🗂 タスク一覧</h2>
            <span className="home-card-note">{(summary?.tasks || []).length}件</span>
          </div>
          {taskGroups.length === 0 ? (
            <p className="home-empty">タスクがまだ登録されていません{isAdmin ? '（設定 → タスク管理 から追加できます）' : ''}</p>
          ) : (
            <div className="home-task-groups">
              {taskGroups.map(([folder, list]) => (
                <div key={folder || '_root'} className="home-task-group">
                  {folder && <h3 className="home-task-folder">📁 {folder}</h3>}
                  <div className="home-task-chips">
                    {list.map(t => (
                      <button key={t.task_id} className="home-task-chip" onClick={() => onStartTask(t.task_id)}
                        title={t.description || `${t.task_name} を始める`}>
                        <TaskDot color={t.color} />{t.task_name}
                        <span className="home-task-chip-go">▶</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {showComposer && (
        <AnnouncementComposer
          members={summary?.team?.members || []}
          onClose={() => setShowComposer(false)}
          onPosted={() => { setShowComposer(false); fetchAnnouncements(); }}
        />
      )}

      {deleteTarget && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header"><h2>🗑 指示の削除</h2></div>
            <div className="dialog-body">
              <p>「<strong>{deleteTarget.title}</strong>」を削除します。よろしいですか？</p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>キャンセル</button>
              <button className="dialog-save" style={{ background: '#DC2626' }} onClick={confirmDelete} disabled={deleting}>
                {deleting ? (<><span className="btn-inline-spinner" />削除中...</>) : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
